import React, { Fragment } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { useIsMobile } from '@/hooks/use-mobile';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Copy } from 'lucide-react';
// Helper to copy division link and show confirmation
const copyDivisionLink = (divisionId: string, setCopied: (val: boolean) => void) => {
  const baseUrl = window.location.href.split('#')[0];
  navigator.clipboard.writeText(`${baseUrl}#${divisionId}`);
  setCopied(true);
  setTimeout(() => setCopied(false), 2000);
};
import { supabase } from '@/lib/supabaseClient';
import { useProjectContext } from '@/contexts/ProjectContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

// Interface for team with club (for type safety in standings fetch)
interface TeamWithClub {
  id: string;
  name: string;
  club?: {
    logo_path?: string;
  };
}

interface LeagueTableProps {
  division: { id: string; name: string };
  className?: string;
  boldLineBetweenRows?: number[];
}

const LeagueTable: React.FC<LeagueTableProps> = ({ division, className, boldLineBetweenRows }) => {
  if (!division) {
    return (
      <Card className={cn('overflow-hidden w-full', className)}>
        <CardHeader className="bg-gray-100 pb-1">
          <CardTitle className="text-lg font-medium">Loading division...</CardTitle>
        </CardHeader>
        <CardContent className="p-4 text-center text-muted-foreground">
          Please wait while we load division data.
        </CardContent>
      </Card>
    );
  }


  const [tableData, setTableData] = useState<any[]>([]);
  const { project } = useProjectContext();

  const getBoldLinePositions = (): number[] => {
    if (division.name === 'Division 1') return [1, 6];
    if (division.name === 'Division 7') return [2];
    return [2, 6];
  };

  useEffect(() => {
    const fetchStandings = async () => {
      if (!project?.id) return;

      const divisionId = division.id;

      try {
        // Fetch all teams in the division with club logo_path
        const { data: teams, error: teamsError } = await supabase
          .from('teams')
          .select('id, name, club_id, club:club_id(logo_path)')
          .eq('project_id', project.id)
          .eq('division_id', divisionId);

        if (teamsError || !teams) {
          console.error('Error fetching teams:', teamsError);
          return;
        }

        // Create a stats map keyed by team id
        const statsMap: { [key: string]: any } = {};
        (teams as TeamWithClub[]).forEach((team) => {
          statsMap[team.id] = {
            ...team,
            logo_path: team.club?.logo_path,
            played: 0,
            won: 0,
            drawn: 0,
            lost: 0,
            gamePoints: 0,
            points: 0,
            totalPoints: 0,
            position: 0,
            homeGames: 0,
            awayGames: 0,
          };
        });

        // Fetch all results for this division and project (using fixture relation)
        const { data: results, error: resultsError } = await supabase
          .from('results')
          .select('*, fixture:fixture_id(project_id, division_id)')
          .eq('fixture.project_id', project.id)
          .eq('fixture.division_id', divisionId);

        if (resultsError || !results) {
          console.error('Error fetching results:', resultsError);
          return;
        }

        results.forEach((result) => {
          const {
            home_team_id,
            away_team_id,
            home_score,
            away_score,
            bonus_home,
            bonus_away,
          } = result;

          // Update home team
          if (statsMap[home_team_id]) {
            statsMap[home_team_id].homeGames += 1;
            statsMap[home_team_id].played += 1;
            statsMap[home_team_id].gamePoints += home_score || 0;
            statsMap[home_team_id].points += bonus_home || 0;

            if (home_score > 2.5) statsMap[home_team_id].won += 1;
            else if (home_score === 2.5) statsMap[home_team_id].drawn += 1;
            else statsMap[home_team_id].lost += 1;
          }

          // Update away team
          if (statsMap[away_team_id]) {
            statsMap[away_team_id].awayGames += 1;
            statsMap[away_team_id].played += 1;
            statsMap[away_team_id].gamePoints += away_score || 0;
            statsMap[away_team_id].points += bonus_away || 0;

            if (away_score > 2.5) statsMap[away_team_id].won += 1;
            else if (away_score === 2.5) statsMap[away_team_id].drawn += 1;
            else statsMap[away_team_id].lost += 1;
          }
        });

        // Finalize totalPoints
        const finalTable = Object.values(statsMap).map((team) => ({
          ...team,
          totalPoints: team.gamePoints + team.points,
        }));

        const sortedTable = finalTable.sort((a, b) => {
          if (b.totalPoints !== a.totalPoints) {
            return b.totalPoints - a.totalPoints;
          }
          if (b.gamePoints !== a.gamePoints) {
            return b.gamePoints - a.gamePoints;
          }
          return a.name.localeCompare(b.name);
        });

        const finalTableWithPositions = sortedTable.map((team, index) => ({
          ...team,
          position: index + 1,
        }));

        setTableData(finalTableWithPositions);
      } catch (err) {
        console.error('Unexpected error during fetch:', err);
      }
    };

    fetchStandings();
  }, [division.id, project]);
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  
  const lastUpdatedTimestamp = tableData.length > 0 ? tableData[0].lastUpdated : null;
  
  const showSeparatorAfter = (position: number): boolean => {
    return getBoldLinePositions().includes(position);
  };
  
  const handleTeamClick = (team: any) => {
    const slugify = (text: string) =>
      text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '') // Remove non-alphanumeric except space and dash
        .replace(/\s+/g, '-') // Replace spaces with dashes
        .replace(/-+/g, '-'); // Replace multiple dashes with one

    const divisionSlug = slugify(division.name);
    const teamSlug = slugify(team.name);
    navigate(`/projects/${project?.slug}/fixtures?division=${divisionSlug}&team=${teamSlug}`);
  };

  
  // For copy-to-clipboard state
  const [copiedMobile, setCopiedMobile] = useState(false);
  const [copiedDesktop, setCopiedDesktop] = useState(false);
  // Division anchor id
  const divisionId = `division-${division.name.replace(/\s+/g, '-').toLowerCase()}`;

useEffect(() => {
  const hash = window.location.hash;
  if (hash) {
    const scrollToHash = () => {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        const yOffset = -80; // Adjust for sticky headers or spacing
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    };

    // Delay scroll to allow content to render
    setTimeout(scrollToHash, 250);
  }
}, []);

  if (isMobile) {
    return (
      <Fragment>
        <div id={divisionId}>
        <div className="text-sm font-medium text-[#6A7181] mb-1 flex items-center justify-between relative">
          <span className="text-base font-bold text-[#6A7181] ml-4">{division.name}</span>
          <Button
            size="sm"
            variant="outline"
            className="text-xs px-2 py-1 ml-auto"
            onClick={() => copyDivisionLink(divisionId, setCopiedMobile)}
          >
            Copy link
          </Button>
          {copiedMobile && (
            <span className="text-green-600 text-xs ml-2">Copied!</span>
          )}
        </div>
          <Card className={cn('overflow-hidden w-full', className)}>
          <CardContent className="p-0 w-full">
          <Table className="w-full border rounded-md">
            <TableHeader className="bg-gray-100">
              <TableRow className="bg-gray-100">
                <TableHead className="w-[42%] pl-2"></TableHead>
                <TableHead className="text-center w-[8%] px-0">H</TableHead>
                <TableHead className="text-center w-[8%] px-0">A</TableHead>
                <TableHead className="text-center w-[10%] px-1">GP</TableHead>
                <TableHead className="text-center w-[10%] px-1">BP</TableHead>
                <TableHead className="text-center w-[12%] px-1 text-black">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tableData.map((team, index) => (
                <React.Fragment key={team.id}>
                  <TableRow
                  >
                    <TableCell 
                      className="font-medium text-xs truncate max-w-[150px] py-2 hover:text-primary hover:underline cursor-pointer pl-2"
                      onClick={() => handleTeamClick(team)}
                    >
                      {team.logo_path && (
                        <img
                          src={`https://kektmescyfgvwazyljph.supabase.co/storage/v1/object/public/club-logos/${team.logo_path}`}
                          alt="Club Logo"
                          className="inline-block w-6 h-6 mr-2 rounded-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      {team.name}
                    </TableCell>
                  <TableCell className="text-center text-xs px-0 py-2 text-muted-foreground">
                    {team.homeGames ?? 0}
                  </TableCell>
                  <TableCell className="text-center text-xs px-0 py-2 text-muted-foreground">
                    {team.awayGames ?? 0}
                  </TableCell>
                  <TableCell className="text-center text-xs px-1 py-2 text-muted-foreground">
                    {team.gamePoints ?? 0}
                  </TableCell>
                  <TableCell className="text-center text-xs px-1 py-2 text-muted-foreground">
                    {team.points ?? 0}
                  </TableCell>
                  <TableCell className="text-center text-xs px-1 py-2 text-black">
                    {team.totalPoints ?? 0}
                  </TableCell>
                  </TableRow>
                  {showSeparatorAfter(team.position) && (
                    <TableRow className="border-0">
                      <TableCell colSpan={6} className="p-0">
                        <Separator className="border border-gray-300" />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
          </Card>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div id={divisionId}>
        <div className="text-sm font-medium text-[#6A7181] mb-1 flex items-center justify-between relative">
          <span className="text-base font-bold text-[#6A7181] ml-4">{division.name}</span>
          <Button
            size="sm"
            variant="outline"
            className="text-xs px-2 py-1 ml-auto"
            onClick={() => copyDivisionLink(divisionId, setCopiedDesktop)}
          >
            Copy link
          </Button>
          {copiedDesktop && (
            <span className="text-green-600 text-xs ml-2">Copied!</span>
          )}
        </div>
        <Card className={cn('overflow-hidden w-full', className)}>
        <CardContent className="p-0">
          <ScrollArea className="h-full">
          <Table className="border rounded-md">
            <TableHeader className="bg-gray-100">
              <TableRow className="bg-gray-100">
                <TableHead></TableHead>
                <TableHead className="text-center w-12 text-muted-foreground">H</TableHead>
                <TableHead className="text-center w-12 text-muted-foreground">A</TableHead>
                <TableHead className="text-center w-12 text-muted-foreground">GP</TableHead>
                <TableHead className="text-center w-12 text-muted-foreground">BP</TableHead>
                <TableHead className="text-center w-12 text-black">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tableData.map((team) => (
                <React.Fragment key={team.id}>
                  <TableRow
                  >
                    <TableCell 
                      className="font-medium text-sm hover:text-primary hover:underline cursor-pointer"
                      onClick={() => handleTeamClick(team)}
                    >
                      {team.logo_path && (
                        <img
                          src={`https://kektmescyfgvwazyljph.supabase.co/storage/v1/object/public/club-logos/${team.logo_path}`}
                          alt="Club Logo"
                          className="inline-block w-6 h-6 mr-2 rounded-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      {team.name}
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground">{team.homeGames ?? 0}</TableCell>
                    <TableCell className="text-center text-muted-foreground">{team.awayGames ?? 0}</TableCell>
                    <TableCell className="text-center text-muted-foreground">{team.gamePoints ?? 0}</TableCell>
                    <TableCell className="text-center font-medium text-muted-foreground">{team.points ?? 0}</TableCell>
                    <TableCell className="text-center text-black">{team.totalPoints ?? 0}</TableCell>
                  </TableRow>
                  {showSeparatorAfter(team.position) && (
                    <TableRow className="border-0">
                      <TableCell colSpan={7} className="p-0">
                        <Separator className="border border-gray-300" />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      </CardContent>
        </Card>
      </div>
    </Fragment>
  );
};

export default LeagueTable;
